import { apiFetch } from './core'

// ============================================
// TEMPLATES
// ============================================

export async function fetchTemplates(filters = {}) {
  const params = new URLSearchParams()
  if (filters.relanceType) params.set('relance_type', filters.relanceType)
  if (filters.isActive !== undefined) params.set('is_active', filters.isActive)

  const url = `/api/relances/templates${params.toString() ? '?' + params : ''}`
  return apiFetch(url)
}

export async function fetchTemplate(id) {
  return apiFetch(`/api/relances/templates/${id}`)
}

export async function createTemplate(data) {
  return apiFetch('/api/relances/templates', {
    method: 'POST',
    body: data,
  })
}

export async function updateTemplate(id, data) {
  return apiFetch(`/api/relances/templates/${id}`, {
    method: 'PATCH',
    body: data,
  })
}

export async function deleteTemplate(id) {
  return apiFetch(`/api/relances/templates/${id}`, {
    method: 'DELETE',
  })
}

// ============================================
// PREVIEW
// ============================================

export async function previewCampaign(data) {
  return apiFetch('/api/relances/preview', {
    method: 'POST',
    body: data,
  })
}

// ============================================
// CAMPAIGNS
// ============================================

export async function fetchCampaigns(filters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.relanceType) params.set('relance_type', filters.relanceType)
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', filters.page)
  if (filters.limit) params.set('limit', filters.limit)

  const url = `/api/relances/campaigns${params.toString() ? '?' + params : ''}`
  return apiFetch(url)
}

export async function fetchCampaign(id) {
  return apiFetch(`/api/relances/campaigns/${id}`)
}

export async function createCampaign(data) {
  return apiFetch('/api/relances/campaigns', {
    method: 'POST',
    body: data,
  })
}

export async function updateCampaign(id, data) {
  return apiFetch(`/api/relances/campaigns/${id}`, {
    method: 'PATCH',
    body: data,
  })
}

export async function deleteCampaign(id) {
  return apiFetch(`/api/relances/campaigns/${id}`, {
    method: 'DELETE',
  })
}

export async function prepareCampaign(id) {
  return apiFetch(`/api/relances/campaigns/${id}/prepare`, {
    method: 'POST',
  })
}

export async function launchCampaign(id, options = {}) {
  return apiFetch(`/api/relances/campaigns/${id}/launch`, {
    method: 'POST',
    body: options,
  })
}

export async function cancelCampaign(id) {
  return apiFetch(`/api/relances/campaigns/${id}/cancel`, {
    method: 'POST',
  })
}

export async function retryFailedCampaign(id) {
  return apiFetch(`/api/relances/campaigns/${id}/retry-failed`, {
    method: 'POST',
  })
}

// ============================================
// RECIPIENTS
// ============================================

export async function fetchRecipients(campaignId, filters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.channel) params.set('channel', filters.channel)
  if (filters.search) params.set('search', filters.search)
  if (filters.page) params.set('page', filters.page)
  if (filters.limit) params.set('limit', filters.limit)

  const url = `/api/relances/campaigns/${campaignId}/recipients${params.toString() ? '?' + params : ''}`
  return apiFetch(url)
}

export async function fetchRecipient(id) {
  return apiFetch(`/api/relances/recipients/${id}`)
}

export async function updateRecipientStatus(id, data) {
  return apiFetch(`/api/relances/recipients/${id}/status`, {
    method: 'PATCH',
    body: data,
  })
}

export async function updateRecipientSelection(id, isSelected) {
  return apiFetch(`/api/relances/recipients/${id}/selection`, {
    method: 'PATCH',
    body: { isSelected },
  })
}

export async function bulkUpdateRecipientSelection(campaignId, recipientIds, isSelected) {
  return apiFetch(`/api/relances/campaigns/${campaignId}/recipients/selection`, {
    method: 'PATCH',
    body: { recipientIds, isSelected },
  })
}

// ============================================
// COMMUNICATIONS LOG
// ============================================

export async function fetchCommunications(filters = {}) {
  const params = new URLSearchParams()
  if (filters.customerId) params.set('customer_id', filters.customerId)
  if (filters.channel) params.set('channel', filters.channel)
  if (filters.sourceType) params.set('source_type', filters.sourceType)
  if (filters.page) params.set('page', filters.page)
  if (filters.limit) params.set('limit', filters.limit)

  const url = `/api/relances/communications${params.toString() ? '?' + params : ''}`
  return apiFetch(url)
}

// ============================================
// QUEUE
// ============================================

export async function fetchQueue(filters = {}) {
  const params = new URLSearchParams()
  if (filters.status) params.set('status', filters.status)
  if (filters.channel) params.set('channel', filters.channel)
  if (filters.campaignId) params.set('campaignId', filters.campaignId)
  if (filters.search) params.set('search', filters.search)
  if (filters.from_date) params.set('from_date', filters.from_date)
  if (filters.to_date) params.set('to_date', filters.to_date)
  if (filters.page) params.set('page', filters.page)
  if (filters.limit) params.set('limit', filters.limit)

  const url = `/api/relances/queue${params.toString() ? '?' + params : ''}`
  return apiFetch(url)
}

export async function cloneCampaign(id) {
  return apiFetch(`/api/relances/campaigns/${id}/clone`, { method: 'POST' })
}

export async function exportCampaignCsv(id, campaignName) {
  const token =
    document.cookie.match(/(?:^|; )token=([^;]*)/)?.[1] || localStorage.getItem('token') || ''
  const res = await fetch(`/api/relances/campaigns/${id}/export.csv`, {
    headers: { Authorization: `Bearer ${token}` },
  })
  if (!res.ok) throw new Error('Export failed')
  const blob = await res.blob()
  const url = URL.createObjectURL(blob)
  const a = document.createElement('a')
  a.href = url
  a.download = `campagne-${campaignName || id}.csv`
  document.body.appendChild(a)
  a.click()
  a.remove()
  URL.revokeObjectURL(url)
}

export async function updateRecipientNote(id, note) {
  return apiFetch(`/api/relances/recipients/${id}/note`, {
    method: 'PATCH',
    body: { note },
  })
}

// ============================================
// VARIABLES
// ============================================

export async function fetchVariables() {
  return apiFetch('/api/relances/variables')
}

// ============================================
// ANALYTICS
// ============================================

export async function fetchAnalytics(filters = {}) {
  const params = new URLSearchParams()
  if (filters.from) params.set('from', filters.from)
  if (filters.to) params.set('to', filters.to)
  return apiFetch(`/api/relances/analytics${params.toString() ? '?' + params : ''}`)
}

// ============================================
// TEST EMAIL
// ============================================

export async function sendTestEmail(payload) {
  return apiFetch('/api/relances/test-send', {
    method: 'POST',
    body: payload,
  })
}
